// Date and time utilities
//
// The backend stores timestamps in UTC and serialises them with isoformat(), which
// often omits the timezone designator (e.g. "2026-06-30T16:22:07.200795"). A bare
// naive string is parsed inconsistently by browsers (and microseconds can yield
// "Invalid Date"). parseServerDate normalises that: any naive datetime is treated as
// UTC, then formatting uses the browser's LOCAL timezone — so a user in India, Dubai,
// Switzerland or the USA each sees the correct local time automatically.
export const parseServerDate = (value) => {
  if (!value) return null
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value
  let s = String(value).trim()
  // Datetime with no timezone (no trailing Z and no +HH:MM / -HH:MM offset) → assume UTC.
  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s) && !/(z|[+-]\d{2}:?\d{2})$/i.test(s)) {
    s = s.replace(' ', 'T') + 'Z'
  }
  const d = new Date(s)
  return isNaN(d.getTime()) ? null : d
}

// Standard project-wide date format: DD-Mon-YYYY (e.g. "07-Jun-2026"). Uses the user's LOCAL
// timezone. The explicit day/month/year assembly guarantees the dd-Mon-yyyy shape regardless
// of the viewer's locale (en-GB gives "07 Jun 2026"; we normalise the separators to dashes).
export const formatDate = (date) => {
  const d = parseServerDate(date)
  if (!d) return '—'
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  }).formatToParts(d)
  const get = (t) => (parts.find(p => p.type === t) || {}).value || ''
  return `${get('day')}-${get('month')}-${get('year')}`  // 07-Jun-2026
}

// Fallback map for browsers/locales where Intl.DateTimeFormat's timeZoneName still returns a
// raw offset (e.g. "GMT+5:30") instead of an abbreviation — this happens when the OS locale
// isn't region-specific (e.g. plain "en" instead of "en-IN"). Covers the zones this team's
// consultants/clients are actually in; falls through to whatever Intl returned otherwise.
const TZ_OFFSET_FALLBACK = {
  'GMT+5:30': 'IST',   // India
  'GMT+4': 'GST',      // UAE / Dubai
  'GMT+4:00': 'GST',
  'GMT+3': 'EAT',      // East Africa (Ethiopia, Kenya)
  'GMT+3:00': 'EAT',
  'GMT+0': 'UTC',
  'GMT+0:00': 'UTC',
}

// DD-Mon-YYYY, h:mm AM/PM + the viewer's timezone abbreviation (e.g.
// "07-Jun-2026, 3:45 PM IST"). Same moment shown in each viewer's local zone.
export const formatDateTime = (date) => {
  const d = parseServerDate(date)
  if (!d) return '—'
  // Date + time assembled with en-GB so the shape is always "07-Jun-2026, 11:31 PM".
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(d)
  const get = (t) => (parts.find(p => p.type === t) || {}).value || ''
  const day = get('day'), month = get('month'), year = get('year')
  const hour = get('hour'), minute = get('minute')
  const ampm = (get('dayPeriod') || '').toUpperCase()
  // Timezone abbreviation uses the VIEWER's own locale (not hardcoded en-GB), so an India
  // (en-IN) user sees "IST" rather than the confusing "GMT+5:30", a US user sees "EST", etc.
  // If the locale isn't region-specific enough for Intl to resolve an abbreviation, fall back
  // to a known-zone lookup keyed on the raw offset it DID return.
  let tz = ''
  try {
    const tzParts = new Intl.DateTimeFormat(undefined, { hour: 'numeric', timeZoneName: 'short' }).formatToParts(d)
    tz = (tzParts.find(p => p.type === 'timeZoneName') || {}).value || ''
    tz = TZ_OFFSET_FALLBACK[tz] || tz
  } catch { tz = '' }
  return `${day}-${month}-${year}, ${hour}:${minute} ${ampm} ${tz}`.replace(/\s+/g, ' ').trim()
}

export const getRelativeTime = (date) => {
  const targetDate = parseServerDate(date)
  if (!targetDate) return '—'

  const now = new Date()
  const diffInSeconds = Math.floor((now - targetDate) / 1000)
  
  if (diffInSeconds < 60) return 'Just now'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`
  
  return formatDate(date)
}

// WHAT "ACTIVE" MEANS. Open work: planned or under way. Four dashboards each decided this for
// themselves and two of them said "In Progress" only, so an Admin and a PM read different
// numbers off the same projects under the same word. One definition, imported everywhere.
export const ACTIVE_STATUSES = ['Planning', 'In Progress'];
export const isActiveProject = (p) => ACTIVE_STATUSES.includes(p?.status);

export const isOverdue = (dueDate) => {
  if (!dueDate) return false
  return new Date(dueDate) < new Date()
}

export const getDaysUntildeadline = (deadline) => {
  if (!deadline) return null
  
  const now = new Date()
  const deadlineDate = new Date(deadline)
  const diffInDays = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24))
  
  return diffInDays
}

// Currency and number formatting
export const formatCurrency = (amount, currency = 'USD', options = {}) => {
  if (amount === null || amount === undefined) return 'N/A'
  
  const defaultOptions = {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }
  
  return new Intl.NumberFormat('en-US', { ...defaultOptions, ...options }).format(amount)
}

export const formatNumber = (number, options = {}) => {
  if (number === null || number === undefined) return 'N/A'
  
  const defaultOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }
  
  return new Intl.NumberFormat('en-US', { ...defaultOptions, ...options }).format(number)
}

export const formatPercentage = (value, total) => {
  if (!value || !total || total === 0) return '0%'
  return `${Math.round((value / total) * 100)}%`
}

export const calculatePercentage = (value, total) => {
  if (!value || !total || total === 0) return 0
  return Math.round((value / total) * 100)
}

// String utilities
export const truncateText = (text, maxLength = 100, suffix = '...') => {
  if (!text || text.length <= maxLength) return text
  return text.substring(0, maxLength - suffix.length) + suffix
}

export const capitalizeFirstLetter = (str) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const slugify = (text) => {
  return text
    .toLowerCase()
    .replace(/[^\w ]+/g, '')
    .replace(/ +/g, '-')
}

export const generateInitials = (name) => {
  if (!name) return 'U'
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2)
}

// Array utilities
export const sortByProperty = (array, property, direction = 'asc') => {
  return [...array].sort((a, b) => {
    const aVal = a[property]
    const bVal = b[property]
    
    if (aVal < bVal) return direction === 'asc' ? -1 : 1
    if (aVal > bVal) return direction === 'asc' ? 1 : -1
    return 0
  })
}

export const groupBy = (array, key) => {
  return array.reduce((groups, item) => {
    const group = item[key]
    if (!groups[group]) groups[group] = []
    groups[group].push(item)
    return groups
  }, {})
}

export const filterBySearch = (array, searchTerm, searchFields) => {
  if (!searchTerm) return array
  
  const lowercaseSearch = searchTerm.toLowerCase()
  
  return array.filter(item =>
    searchFields.some(field => {
      const value = item[field]
      return value && value.toString().toLowerCase().includes(lowercaseSearch)
    })
  )
}

// Validation utilities
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return re.test(email)
}

export const validateRequired = (value) => {
  return value !== null && value !== undefined && value !== ''
}

export const validateMinLength = (value, minLength) => {
  return value && value.length >= minLength
}

export const validatePositiveNumber = (value) => {
  return !isNaN(value) && parseFloat(value) > 0
}

export const validateDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return false
  return new Date(startDate) <= new Date(endDate)
}

// Status and priority utilities
export const getStatusColor = (status, type = 'project') => {
  const statusColors = {
    project: {
      'Planning': 'bg-yellow-100 text-yellow-800',
      'In Progress': 'bg-blue-100 text-blue-800',
      'Completed': 'bg-green-100 text-green-800',
      'On Hold': 'bg-gray-100 text-gray-800',
      'Cancelled': 'bg-red-100 text-red-800'
    },
    funding: {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'under Review': 'bg-blue-100 text-blue-800',
      'Approved': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Partially Approved': 'bg-orange-100 text-orange-800'
    },
    expense: {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Approved': 'bg-green-100 text-green-800',
      'Paid': 'bg-blue-100 text-blue-800',
      'Rejected': 'bg-red-100 text-red-800'
    },
    user: {
      'Active': 'bg-green-100 text-green-800',
      'Away': 'bg-yellow-100 text-yellow-800',
      'Inactive': 'bg-red-100 text-red-800'
    }
  }
  
  return statusColors[type]?.[status] || 'bg-gray-100 text-gray-800'
}

export const getPriorityColor = (priority) => {
  const priorityColors = {
    'High': 'bg-red-100 text-red-800',
    'Medium': 'bg-yellow-100 text-yellow-800',
    'Low': 'bg-green-100 text-green-800'
  }
  
  return priorityColors[priority] || 'bg-gray-100 text-gray-800'
}

export const getRiskLevelColor = (riskLevel) => {
  const riskColors = {
    'High': 'bg-red-100 text-red-800',
    'Medium': 'bg-yellow-100 text-yellow-800',
    'Low': 'bg-green-100 text-green-800'
  }
  
  return riskColors[riskLevel] || 'bg-gray-100 text-gray-800'
}

// File and download utilities
export const downloadAsJSON = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${filename}.json`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export const downloadAsCSV = (data, filename) => {
  if (!data.length) return
  
  const headers = Object.keys(data[0])
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
  ].join('\n')
  
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${filename}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// Local storage utilities
export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
    return true
  } catch (error) {
    console.error('Error saving to localStorage:', error)
    return false
  }
}

export const getLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch (error) {
    console.error('Error reading from localStorage:', error)
    return defaultValue
  }
}

export const removeLocalStorage = (key) => {
  try {
    localStorage.removeItem(key)
    return true
  } catch (error) {
    console.error('Error removing from localStorage:', error)
    return false
  }
}

// debounce utility
export const debounce = (func, delay) => {
  let timeoutId
  return (...args) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(null, args), delay)
  }
}

// Generate random colors
export const generateRandomColor = () => {
  const colors = [
    'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500',
    'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-cyan-500',
    'bg-orange-500', 'bg-teal-500', 'bg-lime-500', 'bg-emerald-500'
  ]
  return colors[Math.floor(Math.random() * colors.length)]
}

// Budget and financial calculations
export const calculateBudgetUtilization = (spent, allocated) => {
  if (!allocated || allocated === 0) return 0
  return Math.round((spent / allocated) * 100)
}

export const getBudgetStatus = (utilized) => {
  if (utilized >= 95) return { status: 'Critical', color: 'red' }
  if (utilized >= 85) return { status: 'Warning', color: 'yellow' }
  if (utilized >= 60) return { status: 'Good', color: 'green' }
  return { status: 'Low Usage', color: 'blue' }
}

export const calculateROI = (revenue, investment) => {
  if (!investment || investment === 0) return 0
  return Math.round(((revenue - investment) / investment) * 100)
}

// Project progress calculations
export const calculateProjectHealth = (project) => {
  const { progress, spentBudget, approvedBudget, endDate } = project
  
  const budgetUtilization = calculateBudgetUtilization(spentBudget, approvedBudget)
  const timeElapsed = calculateTimeProgress(project.startDate, endDate)
  
  let health = 'Good'
  let issues = []
  
  // Budget vs Progress analysis
  if (budgetUtilization > progress + 10) {
    health = 'Warning'
    issues.push('Budget utilization exceeds progress')
  }
  
  // Timeline analysis
  if (timeElapsed > progress + 15) {
    health = 'Critical'
    issues.push('Behind schedule')
  }
  
  if (budgetUtilization > 95 && progress < 90) {
    health = 'Critical'
    issues.push('Budget nearly exhausted')
  }
  
  return { health, issues, budgetUtilization, timeElapsed }
}

export const calculateTimeProgress = (startDate, endDate) => {
  const now = new Date()
  const start = new Date(startDate)
  const end = new Date(endDate)
  
  const totalTime = end - start
  const elapsedTime = now - start
  
  return Math.round((elapsedTime / totalTime) * 100)
}

// Export all utilities as default object
export default {
  formatDate,
  formatDateTime,
  getRelativeTime,
  isOverdue,
  getDaysUntildeadline,
  formatCurrency,
  formatNumber,
  formatPercentage,
  calculatePercentage,
  truncateText,
  capitalizeFirstLetter,
  slugify,
  generateInitials,
  sortByProperty,
  groupBy,
  filterBySearch,
  validateEmail,
  validateRequired,
  validateMinLength,
  validatePositiveNumber,
  validateDateRange,
  getStatusColor,
  getPriorityColor,
  getRiskLevelColor,
  downloadAsJSON,
  downloadAsCSV,
  setLocalStorage,
  getLocalStorage,
  removeLocalStorage,
  debounce,
  generateRandomColor,
  calculateBudgetUtilization,
  getBudgetStatus,
  calculateROI,
  calculateProjectHealth,
  calculateTimeProgress
}
